import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import {
  useApi, formatNumber, formatPercent, formatDuration, timeAgo,
  channelLabel,
} from '../hooks/useApi';
import {
  Card, Metric, RangePicker, BarList, EmptyState, ErrorState, TableSkeleton, ChannelTag,
} from '../components/ui';
import ChannelLedger from '../components/ChannelLedger';

const FEED_ICON = {
  visit: { icon: 'fa-eye', bg: '#eff8ff', color: '#175cd3' },
  enquiry: { icon: 'fa-inbox', bg: '#fef3f2', color: '#d92d20' },
  application: { icon: 'fa-graduation-cap', bg: '#ecfdf3', color: '#067647' },
  subscriber: { icon: 'fa-envelope', bg: '#fffaeb', color: '#b54708' },
};

export default function Dashboard() {
  const [range, setRange] = useState('30d');
  const [ledgerMetric, setLedgerMetric] = useState('visits');
  const [channel, setChannel] = useState(null);

  const params = useMemo(() => ({ range }), [range]);

  const overview = useApi('/api/admin/analytics/overview', params);
  const channels = useApi('/api/admin/analytics/channels', params);
  const series = useApi('/api/admin/analytics/timeseries',
    useMemo(() => ({ range, channel: channel || undefined }), [range, channel]));
  const pages = useApi('/api/admin/analytics/pages', params);
  const services = useApi('/api/admin/analytics/services', params);
  const live = useApi('/api/admin/analytics/live', null, { refreshMs: 60000 });

  const totals = overview.data?.totals;

  // Recharts needs plain values; format the axis rather than the data.
  const chartData = (series.data?.series || []).map((row) => ({
    ...row,
    label: new Date(row.bucket).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
  }));

  if (overview.error) {
    return <ErrorState message={overview.error} onRetry={overview.reload} />;
  }

  return (
    <div className="spa-stack">
      {/* ------------------------------------------------------- controls */}
      <div className="spa-row" style={{ flexWrap: 'wrap' }}>
        <RangePicker value={range} onChange={setRange} />
        {channel && (
          <button type="button" className="spa-btn spa-btn--sm" onClick={() => setChannel(null)}>
            <i className="fa-solid fa-xmark" aria-hidden="true" />
            Clear {channelLabel(channel)} filter
          </button>
        )}
        <div style={{ marginLeft: 'auto' }} className="spa-row">
          <span className="spa-live-dot" aria-hidden="true" />
          <span style={{ fontSize: '0.82rem', color: '#667085' }}>
            <span className="spa-num" style={{ fontWeight: 600, color: '#14161a' }}>
              {formatNumber(live.data?.active_sessions ?? 0)}
            </span>{' '}
            active in the last 30 minutes
          </span>
        </div>
      </div>

      {/* -------------------------------------------------------- metrics */}
      {overview.isLoading && !overview.data ? (
        <div className="spa-skeleton" style={{ height: 108 }} />
      ) : (
        <div className="spa-metrics">
          {(overview.data?.metrics || []).slice(0, 6).map((m) => (
            <Metric key={m.key} label={m.label} value={m.value} change={m.change} />
          ))}
          <Metric
            label="Visit → lead"
            value={formatPercent(totals?.conversion_rate ?? 0, 2)}
          />
        </div>
      )}

      {/* --------------------------------------------------- channel ledger */}
      <Card
        title="Channel ledger"
        note="Where this period's traffic and leads came from. Every touchpoint is attributed at the moment it happens."
      >
        {channels.isLoading && !channels.data ? (
          <div className="spa-skeleton" style={{ height: 140 }} />
        ) : (
          <ChannelLedger
            channels={channels.data?.channels || []}
            totals={channels.data?.totals}
            metric={ledgerMetric}
            onMetricChange={setLedgerMetric}
            selected={channel}
            onSelect={setChannel}
          />
        )}
      </Card>

      {/* ---------------------------------------------------------- trend */}
      <Card
        title={channel ? `Activity — ${channelLabel(channel)}` : 'Activity over time'}
        note="Page views against the enquiries, applications and signups they produced."
      >
        {series.isLoading && !series.data ? (
          <div className="spa-skeleton" style={{ height: 260 }} />
        ) : chartData.length === 0 ? (
          <EmptyState icon="fa-chart-area" title="No activity in this period" />
        ) : (
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1a1d2e" stopOpacity={0.16} />
                    <stop offset="100%" stopColor="#1a1d2e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#e8ebf0" vertical={false} />
                <XAxis
                  dataKey="label" tickLine={false} axisLine={false}
                  tick={{ fontSize: 11, fill: '#98a2b3', fontFamily: 'IBM Plex Mono' }}
                  minTickGap={26}
                />
                <YAxis
                  tickLine={false} axisLine={false}
                  tick={{ fontSize: 11, fill: '#98a2b3', fontFamily: 'IBM Plex Mono' }}
                />
                <Tooltip
                  contentStyle={{
                    border: '1px solid #d8dce5', borderRadius: 5, fontSize: 13,
                    fontFamily: 'Inter, sans-serif', boxShadow: 'none',
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Area type="monotone" dataKey="page_views" name="Page views"
                  stroke="#1a1d2e" strokeWidth={1.8} fill="url(#gViews)" />
                <Area type="monotone" dataKey="enquiries" name="Enquiries"
                  stroke="#d92d20" strokeWidth={1.8} fill="none" />
                <Area type="monotone" dataKey="applications" name="Applications"
                  stroke="#0e9384" strokeWidth={1.8} fill="none" />
                <Area type="monotone" dataKey="subscribers" name="Signups"
                  stroke="#f79009" strokeWidth={1.8} fill="none" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* ----------------------------------------------- pages + live feed */}
      <div className="spa-grid spa-grid--2-1">
        <Card title="Pages" note="Which pages earn their place — views against the leads they generate." padded={false}>
          {pages.isLoading && !pages.data ? (
            <TableSkeleton cols={5} />
          ) : !pages.data?.length ? (
            <EmptyState icon="fa-file-lines" title="No page views yet" />
          ) : (
            <div className="spa-tablewrap">
              <table className="spa-table">
                <thead>
                  <tr>
                    <th>Page</th>
                    <th className="num">Views</th>
                    <th className="num">Visitors</th>
                    <th className="num">Avg time</th>
                    <th className="num">Leads</th>
                    <th className="num">Conv.</th>
                  </tr>
                </thead>
                <tbody>
                  {pages.data.map((p) => (
                    <tr key={p.page}>
                      <td className="spa-table__primary">{p.page}</td>
                      <td className="num">{formatNumber(p.views)}</td>
                      <td className="num">{formatNumber(p.unique_visitors)}</td>
                      <td className="num">{formatDuration(p.avg_seconds)}</td>
                      <td className="num">{formatNumber(p.leads)}</td>
                      <td className="num" style={{ color: p.conversion_rate > 0 ? '#067647' : '#98a2b3' }}>
                        {formatPercent(p.conversion_rate, 1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card
          title="Last two hours"
          note="Everything that happened on the site, newest first."
          padded={false}
        >
          {!live.data?.feed?.length ? (
            <EmptyState icon="fa-clock" title="Quiet right now"
              text="Visits and submissions appear here as they happen." />
          ) : (
            <ul className="spa-feed">
              {live.data.feed.slice(0, 25).map((item, i) => {
                const style = FEED_ICON[item.kind] || FEED_ICON.visit;
                return (
                  <li key={`${item.kind}-${item.created_at}-${i}`}>
                    <span
                      className="spa-feed__kind"
                      style={{ background: style.bg, color: style.color }}
                      aria-hidden="true"
                    >
                      <i className={`fa-solid ${style.icon}`} />
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <div className="spa-table__primary" style={{
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {item.title || '—'}
                      </div>
                      <div className="spa-table__sub">
                        {item.kind === 'visit' ? 'viewed' : item.kind}
                        {item.detail ? ` · ${item.detail}` : ''}
                      </div>
                    </span>
                    <span className="spa-feed__when">{timeAgo(item.created_at)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      {/* --------------------------------------------------- service split */}
      <div className="spa-grid spa-grid--2">
        <Card
          title="Enquiries by service"
          note="What people are writing in about."
          actions={<Link className="spa-btn spa-btn--sm" to="/admin/enquiries">Open enquiries</Link>}
        >
          <BarList items={services.data?.by_service || []} />
        </Card>

        <Card
          title="Applications by track"
          note="Which internship tracks are drawing candidates."
          actions={<Link className="spa-btn spa-btn--sm" to="/admin/applications">Open applications</Link>}
        >
          <BarList items={services.data?.by_track || []} />
        </Card>
      </div>

      {/* --------------------------------------------------------- summary */}
      {totals && (
        <Card title="This period at a glance">
          <div className="spa-grid spa-grid--3">
            <div>
              <div className="spa-metric__label">Sessions</div>
              <div className="spa-num" style={{ fontSize: '1.3rem', fontWeight: 600 }}>
                {formatNumber(totals.sessions)}
              </div>
            </div>
            <div>
              <div className="spa-metric__label">Bounce rate</div>
              <div className="spa-num" style={{ fontSize: '1.3rem', fontWeight: 600 }}>
                {formatPercent(totals.bounce_rate)}
              </div>
            </div>
            <div>
              <div className="spa-metric__label">Avg time on page</div>
              <div className="spa-num" style={{ fontSize: '1.3rem', fontWeight: 600 }}>
                {formatDuration(totals.avg_seconds)}
              </div>
            </div>
          </div>
          {channel && (
            <p className="spa-card__note" style={{ marginTop: 14 }}>
              Filtered to <ChannelTag channel={channel} />. Totals above cover all channels.
            </p>
          )}
        </Card>
      )}
    </div>
  );
}
